import { Alert, Button, Col, Divider, Form, List, Row, Typography } from 'antd';
import React, { Component } from 'react';
import { RouteComponentProps } from 'react-router';
import { AccessToken } from 'routes/login/redux/reducers';
import { RootState } from 'store/configureStore';
import { login } from '../login/redux/actions';
import { connect } from 'react-redux';
import { QrReader } from 'react-qr-reader';
import { RedoOutlined, RetweetOutlined, SwapOutlined } from '@ant-design/icons';
import './style.css';
import SaleService from 'services/sale';
import { Sale } from 'types/type';
import { clearScannedQR } from 'routes/invoices/redux/actions';

interface DispatchProps {
  login: (username: string, password: string) => void;
  clearScannedQR: () => void;
}
interface StateProps {
  accessToken: AccessToken;
  readonly isFetching: boolean;
  readonly scannedQR: string;
}

interface ModalProps {
  visible: boolean;
  onContinue: (sales: Sale[]) => void;
}

type CameraErrorName = 'NotAllowedError' | 'NotFoundError';

interface ScannerState {
  mode: string;
  loading: boolean;
  sales: Sale[];
  error: string | null;
  camError: CameraErrorName | null;
  success: string | null;
}
const saleService = new SaleService();
type Props = DispatchProps & StateProps & RouteComponentProps & ModalProps;

class Scanner extends Component<Props> {
  private cacheKey: string;
  private ttlMinutes = 10;
  public CAM_ERRORS: Record<CameraErrorName, string> = {
    NotAllowedError: 'Error al inicializar la cámara, permisos denegados',
    NotFoundError: 'No se ha encontrado ninguna cámara',
  };
  public isMobile = /Mobi|Android/i.test(navigator.userAgent);
  public RFC_GENERAL = 'XAXX010101000';
  public state: ScannerState = {
    mode: this.isMobile ? 'environment' : 'user',
    loading: false,
    sales: [],  
    error: null,
    success: null,
    camError: null,
  };

  constructor(props: Props) {
    super(props);
    this.cacheKey = `qrSalesCache:${window.location.href}`;
    const cached = localStorage.getItem(this.cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      const now = Date.now();
      if (parsed.expiry > now) {
        this.state.sales = parsed.data;
      } else {
        localStorage.removeItem(this.cacheKey);
        this.state.sales = [];
      }
    }
  }

  private messageTimer: ReturnType<typeof setTimeout> | undefined;

  public componentDidMount(): void {
    this.loadQrArea();
  }

  loadQrArea = async () => {
    const { scannedQR } = this.props;
    if (scannedQR !== '') {
      this.processQR(scannedQR);
      await this.props.clearScannedQR();
    }
  };

  componentDidUpdate(prevProps: {}, prevState: ScannerState) {
    if (this.state.error && this.state.error !== prevState.error) {
      if (this.messageTimer) clearTimeout(this.messageTimer);

      this.messageTimer = setTimeout(() => {
        this.setState({ error: null });
      }, 3000);
    }
    if (this.state.success && this.state.success !== prevState.success) {
      if (this.messageTimer) clearTimeout(this.messageTimer);
      
      this.messageTimer = setTimeout(() => {
        this.setState({ success: null });
      }, 3000);
    }
    if (this.state.sales !== prevState.sales) {
      if(this.state.sales.length > 0){
        const expiry = Date.now() + this.ttlMinutes * 60 * 1000;
        localStorage.setItem(
          this.cacheKey,
          JSON.stringify({ data: this.state.sales, expiry })
        );
      }else{
        localStorage.removeItem(this.cacheKey);
      }
    }
  }

  componentWillUnmount() {
    if (this.messageTimer) clearTimeout(this.messageTimer);
  }

  public processErrorQR = (error: Error) => {
    if (error.name === 'NotAllowedError' || error.name === 'NotFoundError') {
      this.setState({
        camError: error.name,
      });
    } else {
      if (this.state.camError !== null) {
        this.setState({ camError: null });
      }
    }
  };

  public processQR = (readed: string) => {
    if (this.state.loading || this.state.success || this.state.error) {
      return;
    }
    this.setState((prev) => ({
      loading: true,
    }));
    let params = null;
    let query = readed;
    if (readed.includes('?')) {
      query = readed.split('?')[1];
    }
    try {
      params = new URLSearchParams(query);
    } catch (error) {
      this.setState({
        loading: false,
        error: 'El QR leído no tiene formato válido',
      });
      return;
    }
    const iusCode = params.get('iusCode');
    const amount = params.get('amount');
    if (iusCode == null || amount == null) {
      this.setState({
        loading: false,
        error: 'El QR leído no tiene formato válido',
      });
      return;
    }
    const alreadyExists = this.state.sales.some(
      (s: Sale) => s.IUSCode === iusCode,
    );
    if (alreadyExists) {
      this.setState({
        loading: false,
        error: `IUS ${iusCode} previamente añadido`,
      });
      return;
    } else {
      this.loadIusInfo(iusCode || '', amount || '');
    }
  };
  public async loadIusInfo(iusCode: string, amount: string) {
    const amountNum = Number(amount);
    if (iusCode === '') {
      this.setState({
        loading: false,
        error: 'IUS inválido',
      });
      return;
    }
    if (isNaN(amountNum)) {
      this.setState({
        loading: false,
        error: 'Monto inválido',
      });
      return;
    }
    saleService
      .getbyiUSCodeandTotal(iusCode, amountNum)
      .then((response: Sale) => {
        if (
          response.billed &&
          !this.RFC_GENERAL.includes(response.invoiceRfc)
        ) {
          this.setState({
            error: `El IUS ${iusCode} ya se encuentra facturado`,
          });
        } else {
          response._index = this.state.sales.length + 1;
          this.setState({
            sales: [...this.state.sales, response],
            error: undefined,
            success: `IUS ${iusCode} agregado correctamente`,
          });
        }
      })
      .catch((e) => {
        this.setState({
          error: e.response.data.message,
        });
      })
      .finally(() => this.setState({ loading: false }));
  }

  public handleClick = () => {
    const sales = [...this.state.sales];
    this.props.onContinue(sales);
    this.setState({
      sales: [],
      loading: false,
      error: undefined,
    });
  };
  
  private handleDelete = (sale: Sale) => {
    const sales = this.state.sales.filter(s => s.id !== sale.id);
    this.setState({
      sales,
    });
  };
  
  private handleClear = () => {
    this.setState({
      sales: [],
    });
  };

  public render = (): JSX.Element => {
    const { mode, loading, error, sales, success, camError } =
      this.state;

    const sortedSales = [...sales].sort(
      (a: Sale, b: Sale) => b._index - a._index,
    );

    if (!this.props.visible) return <></>;

    return (
      <div className="fs-modal-overlay">
        <div className="modal-content-wrapper">
          <Row gutter={[5, 5]} style={{ width: '100%' }}>
            <Col
              xs={{ span: 24, offset: 0 }}
              sm={{ span: 24, offset: 0 }}
              md={{ span: 24, offset: 0 }}
              lg={{ span: 10, offset: 3 }}
              xl={{ span: 10, offset: 3 }}
              xxl={{ span: 10, offset: 3 }}
            >
              <div
                style={{
                  backgroundColor: 'rgba(36, 32, 32, 1)',
                  borderRadius: 5,
                }}
              >
                <QrReader
                  key={mode}
                  onResult={(result, error) => {
                    if (!!result) {
                      this.processQR(result.getText());
                    }
                    if (!!error) {
                      this.processErrorQR(error);
                    }
                  }}
                  containerStyle={{
                    maxHeight: 420,
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                  }}
                  videoStyle={{
                    maxHeight: 420,
                  }}
                  videoContainerStyle={{
                    maxHeight: 420,
                  }}
                  scanDelay={500}
                  constraints={{ facingMode: mode }}
                />
              </div>
              <div className="send-to-end">
                <Button
                  style={{ marginTop: 5 }}
                  size="large"
                  shape="circle"
                  type="ghost"
                  onClick={() =>
                    this.setState({
                      mode: mode == 'user' ? 'environment' : 'user',
                    })
                  }
                >
                  {camError ? (
                    <RedoOutlined />
                  ) : (
                    <RetweetOutlined rotate={90} />
                  )}
                </Button>
              </div>
            </Col>
            <Col
              xs={{ span: 24, offset: 0 }}
              sm={{ span: 24, offset: 0 }}
              md={{ span: 24, offset: 0 }}
              lg={{ span: 6, offset: 2 }}
              xl={{ span: 6, offset: 2 }}
              xxl={{ span: 6, offset: 2 }}
            >
              {camError && (
                <Alert
                  key={camError && mode}
                  message={
                    <Typography.Text strong>
                      {this.CAM_ERRORS[camError]}
                    </Typography.Text>
                  }
                  type="error"
                  showIcon
                  className="fade"
                />
              )}
              {error && (
                <Alert
                  message={<Typography.Text>{error}</Typography.Text>}
                  type="error"
                  showIcon
                  className="fade"
                />
              )}
              {success && (
                <Alert
                  message={<Typography.Text>{success}</Typography.Text>}
                  type="success"
                  showIcon
                  className="fade"
                />
              )}
              <Divider orientation="left">
                <Typography.Text strong>IUS a añadir</Typography.Text>
                <Divider type='vertical'/>
                <Button
                  type="dashed"
                  onClick={this.handleClear}
                  icon='delete'
                >Limpiar</Button>
              </Divider>
              <List
                loading={loading}
                size="small"
                bordered
                style={{ borderRadius: 5 }}
                dataSource={sortedSales}
                extra={{}}
                pagination={{
                  pageSize: 5,
                  position: 'bottom',
                  size: 'small',
                }}
                renderItem={(sale: Sale) => (
                  <List.Item>
                    <Typography.Text style={{ marginRight: 8 }}>
                      {sale._index}
                    </Typography.Text>
                    <Typography.Text strong>{sale.IUSCode}</Typography.Text>
                    <Button
                      type="danger"
                      onClick={() => this.handleDelete(sale)}
                      icon='delete'
                    />
                  </List.Item>
                )}
              />
              <Divider />
              <Row className="send-to-end" gutter={10}>
                <Col>
                  <Button
                    type="default"
                    onClick={() => this.props.onContinue([])}
                  >
                    Cancelar
                  </Button>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    onClick={this.handleClick}
                  >
                    Continuar
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </div>
    );
  };
}

const mapStateToProps = (states: RootState): StateProps => {
  return {
    accessToken: states.loginState.accessToken,
    isFetching: states.invoiceState.isFetching,
    scannedQR: states.invoiceState.scannedQR,
  };
};

/**
 * Maps the possible actions that can be executed in this component.
 */
const mapDispatchToProps = {
  login,
  clearScannedQR,
};

export default connect<StateProps, DispatchProps, ModalProps, RootState>(
  mapStateToProps,
  mapDispatchToProps,
)(Form.create()(Scanner));
